import { download } from 'libs/jamUtil'

import { fileToBase64 } from 'utils'

import type { Attachment } from 'pages/main/components/Attachment/helper'

export interface Base64AttachmentItem {
  filename: string
  content_base64: string
  content_type: string
  content_id?: string
}

export type JmapBodyValues = Record<string, { value: string } | undefined> | null | undefined

const DEFAULT_CONTENT_TYPE = 'application/octet-stream'

const resolveContentTypeFromBlob = (blob: Blob, fallback: string): string => {
  if (blob.type != null && blob.type.length > 0) return blob.type
  return fallback
}

const buildItem = async (
  att: Attachment,
  bodyValues: JmapBodyValues,
): Promise<Base64AttachmentItem | null> => {
  // 1) 새로 추가된 첨부 → 기존 동작 유지.
  if (att.originFile != null) {
    const originFile = att.originFile as File
    const base64 = await fileToBase64(originFile)
    const item: Base64AttachmentItem = {
      filename: originFile.name,
      content_base64: base64,
      content_type: originFile.type || DEFAULT_CONTENT_TYPE,
    }
    if (att.file.cid != null && att.file.cid.length > 0) {
      item.content_id = att.file.cid
    }
    return item
  }

  // 2) draft에서 재로드된 첨부 → bodyValues 또는 blobId.
  const draftFile = att.draftFile
  if (draftFile == null) {
    console.warn('[buildBase64Attachments] skip: no originFile and no draftFile', att)
    return null
  }

  const filename = draftFile.name || att.file.name || ''
  const contentType = draftFile.type || att.file.type || DEFAULT_CONTENT_TYPE
  const partId = draftFile.partId ?? null

  let base64: string | null = null

  // 2-A) bodyValues에 partId 데이터가 있으면 그대로 사용.
  if (partId != null && bodyValues != null) {
    const entry = bodyValues[partId]
    const value = entry?.value
    if (typeof value === 'string') {
      const blob = new Blob([value], { type: contentType })
      base64 = await fileToBase64(blob)
    }
  }

  // 2-B) bodyValues에 없으면 blobId로 다운로드.
  if (base64 == null) {
    const blobId = draftFile.blobId ?? null
    if (blobId == null) {
      console.warn('[buildBase64Attachments] skip: no partId match and no blobId', draftFile)
      return null
    }
    try {
      const downloadObj = await download({ blobId, name: filename, type: contentType }, false, true)
      const blob: Blob | null = downloadObj?.blob ?? null
      if (blob == null) {
        console.warn('[buildBase64Attachments] skip: download returned no blob', draftFile)
        return null
      }
      const typedBlob =
        blob.type != null && blob.type.length > 0
          ? blob
          : new Blob([blob], { type: resolveContentTypeFromBlob(blob, contentType) })
      base64 = await fileToBase64(typedBlob)
    } catch (err) {
      console.warn('[buildBase64Attachments] download failed', draftFile, err)
      return null
    }
  }

  const item: Base64AttachmentItem = {
    filename,
    content_base64: base64,
    content_type: contentType,
  }
  if (att.file.cid != null && att.file.cid.length > 0) {
    item.content_id = att.file.cid
  }
  return item
}

/**
 * Read‑Receipt / Secure‑Send 같이 base64 페이로드를 직접 보내는 발송 경로에서
 * 사용한다. draft 재로드 첨부(originFile 없음)도 partId → bodyValues 또는 blobId
 * 다운로드를 통해 안전히 base64로 변환한다.
 */
export const buildBase64Attachments = async (
  attachments: Attachment[],
  bodyValues: JmapBodyValues,
): Promise<Base64AttachmentItem[]> => {
  const results = await Promise.all(
    (attachments || [])
      .filter((att) => att.originFile != null || att.draftFile != null)
      .map((att) => buildItem(att, bodyValues)),
  )
  return results.filter((item): item is Base64AttachmentItem => item != null)
}
